import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { InjectionToken } from '@angular/core';
import { forkJoin, of, Subject, timer } from 'rxjs';
import { buffer, catchError, debounceTime, delay, first, skipUntil, switchMap, take, takeUntil } from 'rxjs/operators';
import { LogLevel } from '../logger/log-levels.enum';
import { EventMessage, LogMessage } from '../models/log-message.model';
import { LogEvent } from '../utils/log-event.model';
import { logLevelValuesMap } from '../utils/log-levels-values.map';
import { AbstractTransporterDecorator } from './abstract-transporter-decorator';
import { TransporterConfig } from './transporter-config.model';
import { TransportTemplate } from './transporter-template.model';

export const HttpTransporterConfigToken = new InjectionToken<HttpTransporterConfig>('HttpTransporterConfig');

export enum HttpTransportMethods {
  PUT = 'PUT',
  GET = 'GET',
  POST = 'POST',
}

export interface HttpTransporterConfig extends TransporterConfig {
  url: string;
  httpMethod?: string;
}

interface LogBatch<T> {
  level: LogLevel;
  payload: T;
}

/**
 * Http Transporter
 */
export class HttpTransporter<T extends EventMessage = LogMessage> extends AbstractTransporterDecorator<T> {
  batcher$ = new Subject<LogBatch<T>>();

  constructor(template: TransportTemplate<T>, private httpClient: HttpClient, config: HttpTransporterConfig) {
    super(template, config);
    // create a batcher observable
    const batcher$ = this._batcherMaker$();

    // one time timer observable
    const loadingTimer$ = timer(10001).pipe(first());

    /*
     * Create a batcher to capture any "loading" time error within the first 10 seconds to
     * give preference to app loading processes
     */
    batcher$
      .pipe(
        /*
         * Allow 10 secs as grace time to application to load
         * before publishing logs
         */
        delay(10000),
        switchMap(batches => forkJoin(this.toHttpRequests(batches))),
        takeUntil(loadingTimer$)
      )
      .subscribe();

    /*
     * Create a batcher to capture any error after the first 10 seconds to
     * give preference to app loading processes
     *
     */
    batcher$
      .pipe(
        skipUntil(loadingTimer$),
        switchMap(batches => forkJoin(this.toHttpRequests(batches)))
      )
      .subscribe();
  }

  private _batcherMaker$() {
    return this.batcher$.pipe(buffer(this.batcher$.pipe(debounceTime(500))));
  }

  private toHttpRequests(batches: Array<LogBatch<T>>) {
    return batches.map(batch =>
      this.makeHttpRequest(this.config as HttpTransporterConfig, batch.level, batch.payload)
        // make network call and catch errors
        .pipe(
          take(1),
          catchError(error => {
            console.error('Error while logging message', batch.payload, 'Error:', error);
            return of(null);
          })
        )
    );
  }

  /**
   * Creates http observable with given params (url, httpMethod, level & payload)
   */
  private makeHttpRequest(
    { url, httpMethod = HttpTransportMethods.POST }: HttpTransporterConfig,
    logLevel: LogLevel,
    payload: T
  ) {
    // build object payload (ensure final message is an object)
    const level = payload['level'] || logLevelValuesMap[logLevel];
    const data: LogEvent = { ...this.ensureData(payload), level };
    const headers = new HttpHeaders();
    headers.set('Access-Control-Allow-Origin', '*');
    switch (httpMethod) {
      case 'POST':
        return this.httpClient.post(url, data, { headers });

      case 'PUT':
        return this.httpClient.put(url, data, { headers });

      case 'GET':
        const params = new HttpParams();
        params.set('level', level);
        Object.entries(data)
          .filter(([, value]) => Boolean(value))
          .forEach(([key, value]) => params.set(key, encodeURIComponent(value)));
        return this.httpClient.get(url, { params, headers });
    }
  }

  /**
   * Writes/sends payload to configured url and given Http Method
   */
  doWrite(payload: T, level: LogLevel): void {
    this.batcher$.next({ level, payload });
  }

  private ensureData(message: any) {
    return this.isPrimitive(message) ? { meta: { message } } : message;
  }

  private isPrimitive(message: any): boolean {
    return (
      typeof message === 'string' ||
      typeof message === 'number' ||
      typeof message === 'boolean' ||
      message === null ||
      message instanceof Date ||
      typeof message === 'function'
    );
  }
}
